const normalizeApartmentId = (apartmentId) =>
  String(apartmentId ?? "")
    .split("@")
    .pop();

const getCloudPeriodConfig = (period, referenceDateISO) => {
  if (period === "hourly") {
    return {
      type: "hourly",
      shouldReturnLast: false,
      rangeBlock: `
      refDay = time(v: "${referenceDateISO}")
      day    = date.truncate(t: refDay, unit: 1d)
      start  = day
      stop   = date.add(d: 1d, to: day)
      `,
    };
  }

  if (period === "daily") {
    return {
      type: "daily",
      shouldReturnLast: false,
      rangeBlock: `
      ref   = time(v: "${referenceDateISO}")
      start = date.truncate(t: ref, unit: 1mo)
      stop  = date.add(d: 1mo, to: start)
      `,
    };
  }

  if (period === "monthly") {
    return {
      type: "monthly",
      shouldReturnLast: false,
      rangeBlock: `
      ref   = time(v: "${referenceDateISO}")
      start = date.truncate(t: ref, unit: 1y)
      stop  = date.add(d: 1y, to: start)
      `,
    };
  }

  return {
    type: "monthly",
    shouldReturnLast: true,
    rangeBlock: `
      ref   = time(v: "${referenceDateISO}")
      start = date.truncate(t: ref, unit: 1mo)
      stop  = date.add(d: 1mo, to: start)
      `,
  };
};

// Serie por rango libre de fechas (un apartamento - CLOUD)
const proyectByDatesFlux = ({
  bucket,
  type,
  measurement,
  apartmentId,
  field,
  startISO,
  stopISO,
}) => {
  const normalizedApartmentId = normalizeApartmentId(apartmentId);

  return `
start = time(v: "${startISO}")
stop  = time(v: "${stopISO}")
from(bucket: "${bucket}")
  |> range(start: start, stop: stop)
  |> filter(fn: (r) => r._measurement == "${measurement}")
  |> filter(fn: (r) => r.type == "${type}")
  |> filter(fn: (r) => r.apartmentId == "${normalizedApartmentId}")
  |> filter(fn: (r) => r._field == "${field}")
  |> keep(columns: ["_time","_value","_measurement","type","apartmentId","onnaId","_field"])
  |> sort(columns: ["_time"])
`.trim();
};

// Un valor por apartamento (ALL - CLOUD)
const proyectAllApartFlux = ({
  bucket,
  tz,
  period, // hourly | daily | monthly
  measurement,
  apartmentId,
  field,
  referenceDateISO,
}) => {
  const normalizedApartmentId = normalizeApartmentId(apartmentId);
  const { type, rangeBlock, shouldReturnLast } = getCloudPeriodConfig(period, referenceDateISO);

  return `
      import "timezone"
      import "date"

      option location = timezone.location(name: "${tz}")
${rangeBlock}

from(bucket: "${bucket}")
  |> range(start: start, stop: stop)
  |> filter(fn: (r) => r._measurement == "${measurement}")
  |> filter(fn: (r) => r.type == "${type}")
  |> filter(fn: (r) => r.apartmentId == "${normalizedApartmentId}")
  |> filter(fn: (r) => r._field == "${field}")
  |> keep(columns: ["_time","_value","apartmentId","state"])
  |> sort(columns: ["_time"])
${shouldReturnLast ? "  |> last()" : ""}
`.trim();
};

// Serie completa (un apartamento - CLOUD)
const proyectSingleApartmentFlux = ({
  bucket,
  tz,
  period,
  measurement,
  apartmentId,
  field,
  referenceDateISO,
}) => {
  const normalizedApartmentId = normalizeApartmentId(apartmentId);
  const { type, rangeBlock } = getCloudPeriodConfig(period, referenceDateISO);

  return `
import "timezone"
import "date"

option location = timezone.location(name: "${tz}")
${rangeBlock}

from(bucket: "${bucket}")
  |> range(start: start, stop: stop)
  |> filter(fn: (r) => r._measurement == "${measurement}")
  |> filter(fn: (r) => r.type == "${type}")
  |> filter(fn: (r) => r.apartmentId == "${normalizedApartmentId}")
  |> filter(fn: (r) => r._field == "${field}")
  |> keep(columns: ["_time","_value","_measurement","type","apartmentId","onnaId","_field","state"])
  |> sort(columns: ["_time"])
`.trim();
};

const individualApartDetailFlux = ({
  startISO,
  stopISO,
  measurement,
  individualApartmentId,
  field,
}) =>
  `
start = time(v: "${startISO}")
stop  = time(v: "${stopISO}")
from(bucket: "client")
  |> range(start: start, stop: stop)
  |> filter(fn: (r) => r._measurement == "${measurement}")
  |> filter(fn: (r) => r.type == "raw")
  |> filter(fn: (r) => r.apartmentId == "${individualApartmentId}")
  |> filter(fn: (r) => r._field == "${field}")
  |> keep(columns: ["_time","_value","_measurement","type","apartmentId","_field"])
  |> sort(columns: ["_time"])
`.trim();

const individualApartMonthlyFlux = ({
  referenceDateISO,
  measurement,
  individualApartmentId,
  field,
}) =>
  `
import "date"
ref   = time(v: "${referenceDateISO}")
start = date.truncate(t: ref, unit: 1y)
stop  = date.add(d: 1y, to: start)
from(bucket: "client_processed")
  |> range(start: start, stop: stop)
  |> filter(fn: (r) => r._measurement == "${measurement}")
  |> filter(fn: (r) => r.type == "monthly")
  |> filter(fn: (r) => r.apartmentId == "${individualApartmentId}")
  |> filter(fn: (r) => r._field == "${field}")
  |> keep(columns: ["_time","_value","apartmentId"])
  |> sort(columns: ["_time"])
`.trim();

// Agregados horarios procesados para un día completo (DEV — individual)
const individualApartHourlyFlux = ({
  startISO,
  stopISO,
  measurement,
  individualApartmentId,
  field,
}) =>
  `
import "timezone"
import "date"

start = time(v: "${startISO}")
stop  = time(v: "${stopISO}")
from(bucket: "client_processed")
  |> range(start: start, stop: stop)
  |> filter(fn: (r) => r._measurement == "${measurement}")
  |> filter(fn: (r) => r.type == "hourly")
  |> filter(fn: (r) => r.apartmentId == "${individualApartmentId}")
  |> filter(fn: (r) => r._field == "${field}")
  |> keep(columns: ["_time","_value","apartmentId","state"])
  |> sort(columns: ["_time"])
`.trim();

// Consulta por rango libre de fechas (DEV — individual)
// type = "hourly" cuando period=daily | type = "daily" cuando period=monthly
const individualApartByDatesFlux = ({
  startISO,
  stopISO,
  type,
  measurement,
  individualApartmentId,
  field,
}) =>
  `
start = time(v: "${startISO}")
stop  = time(v: "${stopISO}")
from(bucket: "client_processed")
  |> range(start: start, stop: stop)
  |> filter(fn: (r) => r._measurement == "${measurement}")
  |> filter(fn: (r) => r.type == "${type}")
  |> filter(fn: (r) => r.apartmentId == "${individualApartmentId}")
  |> filter(fn: (r) => r._field == "${field}")
  |> keep(columns: ["_time","_value","apartmentId"])
  |> sort(columns: ["_time"])
`.trim();

// Agregados diarios procesados para un mes completo (DEV — individual)
const individualApartDailyFlux = ({
  referenceDateISO,
  measurement,
  individualApartmentId,
  field,
  tz,
}) => {
  const { rangeBlock } = getCloudPeriodConfig("daily", referenceDateISO);
  return `
import "timezone"
import "date"
option location = timezone.location(name: "${tz}")

${rangeBlock}

from(bucket: "client_processed")
  |> range(start: start, stop: stop)
  |> filter(fn: (r) => r._measurement == "${measurement}")
  |> filter(fn: (r) => r.type == "daily")
  |> filter(fn: (r) => r.apartmentId == "${individualApartmentId}")
  |> filter(fn: (r) => r._field == "${field}")
  |> keep(columns: ["_time","_value","apartmentId"])
  |> sort(columns: ["_time"])
`.trim();
};

const proyectLastState1HourFlux = (metrics = [], apartmentId = null) =>
  `
import "influxdata/influxdb/schema"

m = ${JSON.stringify(metrics)}

from(bucket: "__BUCKET__")
  |> range(start: -1d)
  |> filter(fn: (r) => contains(value: r._measurement, set: m))
  |> filter(fn: (r) => r._field == "value")
  |> filter(fn: (r) => r.type == "hourly")
  ${apartmentId ? `|> filter(fn: (r) => r.apartmentId == "${apartmentId}")` : ""}
  |> group(columns: ["apartmentId", "_measurement", "onnaId"])
  |> sort(columns: ["_time"])
  |> last(column: "_value")
  |> keep(columns: ["_time","_value","_measurement","apartmentId","onnaId"])
`.trim();

const individualLastStateFlux = (individualApartmentId, metrics = []) =>
  `
import "influxdata/influxdb/schema"

m = ${JSON.stringify(metrics)}

from(bucket: "client")
  |> range(start: -1d)
  |> filter(fn: (r) => r.apartmentId == "${individualApartmentId}")
  |> filter(fn: (r) => contains(value: r._measurement, set: m))
  |> filter(fn: (r) => r._field == "value")
  |> filter(fn: (r) => r.type == "raw")
  |> group(columns: ["_measurement"])
  |> sort(columns: ["_time"])
  |> last(column: "_value")
  |> keep(columns: ["_time","_value","_measurement","apartmentId"])
`.trim();

const fluxQuerys = {
  proyectSingleApartmentFlux,
  proyectByDatesFlux,
  proyectAllApartFlux,
  proyectLastState1HourFlux,
  individualApartByDatesFlux,
  individualApartHourlyFlux,
  individualApartDailyFlux,
  individualApartMonthlyFlux,
  individualApartDetailFlux,
  individualLastStateFlux,
};

export default fluxQuerys;
